<script setup>
import { ref, computed, reactive, watch, inject } from 'vue'
import { useUserStore } from '@/stores/UserStore'
import { useConfigStore } from '@/stores/ConfigStore'
import { useBillingStore } from '@/stores/BillingStore'
import { useGlobalStore } from '@/stores/GlobalStore'
import { STRIPE_ERRORS, CARD_BRAND_ICONS } from '@/constants'
import { handleError } from '@/services/errorHandler'
import StripeCardSplit from '@/components/StripeCardSplit.vue'
import Address from '@/components/Address/index.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'

const stripeError = inject('stripeError')

const props = defineProps({
  targetPlan: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['upgrade'])

const userStore = useUserStore()
const configStore = useConfigStore()
const billingStore = useBillingStore()
const globalStore = useGlobalStore()

const expanded = ref(false)
const editingPayment = ref(false)
const editingBilling = ref(false)
const savingPayment = ref(false)
const savingBilling = ref(false)
const billingErrors = ref([])

const billingAddress = reactive({
  first_name: userStore.user?.first_name || '',
  last_name: userStore.user?.last_name || '',
  address_1: userStore.user?.address_1 || '',
  address_2: userStore.user?.address_2 || '',
  city: userStore.user?.city || '',
  state: userStore.user?.state || '',
  postal_code: userStore.user?.postal_code || '',
  country: userStore.user?.country || 'US',
})

const currentPlan = computed(() => billingStore.currentPlan)
const subscription = computed(() => configStore.getConfigField('subscription'))
const haveSavedPayment = computed(() => !!userStore.user?.stripe_default_payment_method)
const savedPaymentLast4 = computed(() => userStore.user?.stripe_default_payment_method?.last4)
const savedPaymentBrand = computed(() => userStore.user?.stripe_default_payment_method?.brand)
const savedPaymentExpMonth = computed(() => userStore.user?.stripe_default_payment_method?.exp_month)
const savedPaymentExpYear = computed(() => userStore.user?.stripe_default_payment_method?.exp_year)

const planName = computed(() => props.targetPlan?.label || 'Majors')
const isAnnual = computed(() => billingStore.plansPeriods.selected === 'annual')
const useAnnualPricing = computed(() =>
  billingStore.upgradeFeatureModalData?.isPlanUpgradeModal
    ? isAnnual.value
    : (configStore.config.subscription?.annual ?? false)
)
const isActiveSubscription = computed(() => configStore.getConfigField('hasActiveSubscription'))

const planCost = computed(() =>
  useAnnualPricing.value
    ? (props.targetPlan?.price.annual_final || 0)
    : (props.targetPlan?.price.monthly || 0)
)

const proratedCharge = computed(() => {
  if (!isActiveSubscription.value) return planCost.value

  const currentCost = useAnnualPricing.value
    ? (currentPlan.value?.price.annual_final || 0)
    : (currentPlan.value?.price.monthly || 0)

  return Math.max(0, planCost.value - currentCost)
})

const toggleExpand = () => {
  expanded.value = !expanded.value
}

const startEditPayment = () => {
  editingPayment.value = true
  stripeError.value = ''
}

const cancelEditPayment = () => {
  editingPayment.value = false
  stripeError.value = ''
}

const savePayment = async () => {
  savingPayment.value = true
  stripeError.value = ''

  try {
    const stripe = globalStore.stripe
    const card = globalStore.card

    if (!stripe || !card) {
      stripeError.value = 'Payment system not loaded. Please try again.'
      savingPayment.value = false
      return
    }

    const { paymentMethod, error } = await stripe.createPaymentMethod({
      type: 'card',
      card,
    })

    if (error) {
      stripeError.value = STRIPE_ERRORS[error.code] || error.message
      savingPayment.value = false
      return
    }

    await billingStore.createPaymentMethod({
      payment_method_id: paymentMethod.id,
    })

    if (billingStore.paymentError?.message && !billingStore.paymentError?.ok) {
      stripeError.value = STRIPE_ERRORS[billingStore.paymentError?.error?.code] || STRIPE_ERRORS[billingStore.paymentError?.error?.decline_code] || billingStore.paymentError?.message
    } else {
      await billingStore.setDefaultPaymentMethod(paymentMethod.id)
      await userStore.fetchUser()
      editingPayment.value = false
    }
  } catch (err) {
    stripeError.value = 'An error occurred. Please try again.'
    handleError(err, { category: 'component', action: 'save-payment', component: 'PlanSection' })
  }

  savingPayment.value = false
}

const startEditBilling = () => {
  billingAddress.first_name = userStore.user?.first_name || ''
  billingAddress.last_name = userStore.user?.last_name || ''
  billingAddress.address_1 = userStore.user?.address_1 || ''
  billingAddress.address_2 = userStore.user?.address_2 || ''
  billingAddress.city = userStore.user?.city || ''
  billingAddress.state = userStore.user?.state || ''
  billingAddress.postal_code = userStore.user?.postal_code || ''
  billingAddress.country = userStore.user?.country || 'US'
  billingErrors.value = []
  editingBilling.value = true
}

const cancelEditBilling = () => {
  editingBilling.value = false
  billingErrors.value = []
}

const saveBilling = async () => {
  const errors = []
  if (!billingAddress.first_name) errors.push('first_name')
  if (!billingAddress.last_name) errors.push('last_name')
  if (!billingAddress.address_1) errors.push('address_1')
  if (!billingAddress.city) errors.push('city')
  if (!billingAddress.postal_code) errors.push('postal_code')
  if (!billingAddress.country) errors.push('country')
  if (!billingAddress.state) errors.push('state')

  billingErrors.value = errors
  if (errors.length) return

  savingBilling.value = true

  try {
    await userStore.updateUser({
      first_name: billingAddress.first_name,
      last_name: billingAddress.last_name,
      address_1: billingAddress.address_1,
      address_2: billingAddress.address_2,
      city: billingAddress.city,
      state: billingAddress.state,
      postal_code: billingAddress.postal_code,
      country: billingAddress.country,
    })
    editingBilling.value = false
  } catch (err) {
    handleError(err, { category: 'component', action: 'save-billing', component: 'PlanSection' })
  }

  savingBilling.value = false
}

const handleUpgradeNow = () => {
  emit('upgrade')
}

const handleComparePlans = () => {
  billingStore.upgradeWindow = true
}

watch(() => userStore.user.stripe_default_payment_method, (newVal) => {
  if (newVal) {
    editingPayment.value = false
  }
});
</script>

<template>
  <div class="bg-gray-100 rounded-[10px] p-5 flex flex-col gap-y-3">
    <!-- Total due today -->
    <section class="flex flex-col gap-y-1">
      <p class="text-sm text-gray-500 font-semibold">Total due today</p>
      <div class="flex items-center justify-between">
        <h2 class="text-3xl font-bold text-gray-900">${{ proratedCharge.toFixed(2) }}</h2>
        <button
          class="p-1 text-gray-400 hover:text-gray-400 transition"
          @click="toggleExpand"
          aria-label="Toggle details"
        >
          <Icon
            icon="heroicons:chevron-up-16-solid"
            class="text-xl transition-transform duration-300"
            :class="{ 'rotate-180': !expanded }"
          />
        </button>
      </div>
      <div class="flex max-md:flex-col md:items-center justify-between gap-y-1">
        <p class="text-base font-semibold text-gray-900">
          Estimated next bill: ${{ planCost.toFixed(2) }}
        </p>
        <button
          class="text-xs text-gray-500 font-semibold hover:underline max-md:w-fit"
          @click="handleComparePlans"
          v-if="!billingStore.upgradeFeatureModalData?.isPlanUpgradeModal"
        >
          Compare plans
        </button>
      </div>
    </section>

    <!-- Expandable details -->
    <Transition name="fade">
      <div v-if="expanded" class="flex flex-col gap-y-3">
        <!-- Plan upgrade breakdown -->
        <section class="flex flex-col gap-y-2">
          <h4 class="text-sm font-semibold text-gray-500">Plan {{ billingStore.currentPlan.id < targetPlan.id ? 'upgrade' : 'downgrade' }}</h4>
          <div class="flex flex-col gap-y-1 text-sm text-gray-500">
            <div class="flex items-center justify-between">
              <span>{{ planName }} Plan (billed {{ useAnnualPricing ? 'annually' : 'monthly' }})</span>
              <span>${{ planCost.toFixed(2) }}/{{ useAnnualPricing ? 'year' : 'month' }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span>Prorated charge</span>
              <span>+${{ proratedCharge.toFixed(2) }}</span>
            </div>
          </div>

          <hr class="border-gray-200"/>

          <div class="flex flex-col gap-y-1 text-sm text-gray-500">
            <div class="flex items-center justify-between">
              <span>Subtotal (prorated)</span>
              <span>${{ proratedCharge.toFixed(2) }}</span>
            </div>
            <div class="flex items-center justify-between">
              <span>Tax if applicable</span>
              <span>{{ userStore.tax?.taxAmount ? `$${userStore.tax.taxAmount.toFixed(2)}` : '-' }}</span>
            </div>
          </div>
        </section>

      </div>
    </Transition>

    <hr v-if="!editingBilling && !editingPayment" class="border-gray-200"/>

    <!-- Billed to / Payment Method (always visible) -->
    <section v-if="!editingBilling && !editingPayment">
      <div class="flex max-md:flex-col gap-6">
        <!-- Billed to -->
        <div class="flex-1 flex flex-col gap-y-2">
          <div class="flex items-center justify-between">
            <h4 class="text-sm font-semibold text-gray-500">Billed to</h4>
            <!-- <button class="text-gray-400 text-sm font-semibold" @click="startEditBilling">
              <Icon icon="heroicons:pencil-solid" class="text-lg" />
            </button> -->
          </div>
          <div class="text-sm text-gray-900 flex flex-col gap-y-0.5">
            <span>{{ userStore.user?.first_name }} {{ userStore.user?.last_name }}</span>
            <span v-if="userStore.user?.address_1">{{ userStore.user?.address_1 }}</span>
            <span v-if="userStore.user?.city || userStore.user?.state || userStore.user?.postal_code">
              {{ [userStore.user?.city, userStore.user?.state, userStore.user?.postal_code].filter(Boolean).join(', ') }}
            </span>
          </div>
        </div>

        <!-- Payment Method -->
        <div class="flex-1">
          <div class="flex items-center justify-between mb-2">
            <h4 class="text-sm font-semibold text-gray-500">Payment Method</h4>
            <button class="text-gray-400 text-sm font-semibold" @click="startEditPayment">
              <Icon icon="heroicons:pencil-solid" class="text-lg" />
            </button>
          </div>
          <div v-if="haveSavedPayment" class="flex gap-2 text-gray-900 font-normal text-sm">
            <div class="flex items-center justify-center w-[28px] h-[18px] p-1 border border-gray-200 rounded-sm">
              <Icon :icon="CARD_BRAND_ICONS[savedPaymentBrand.toLowerCase()]" class="text-base text-gray-400" />
            </div>
            <span>&bull;&bull;&bull;&bull; {{ savedPaymentLast4 }}</span>
            <span>{{ savedPaymentExpMonth }}/{{ savedPaymentExpYear?.toString().slice(-2) }}</span>
          </div>
          <p v-else class="text-sm text-gray-400">No payment method</p>
        </div>
      </div>
    </section>

    <!-- Payment Method editing -->
    <section v-if="editingPayment" class="border-t border-gray-200 pt-4">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-semibold text-gray-500">Payment Method</h4>
        <button class="text-gray-400 hover:text-gray-600" @click="cancelEditPayment">
          <Icon icon="heroicons:arrow-uturn-left" class="text-base" />
        </button>
      </div>
      <div class="bg-white rounded-lg border border-gray-200 p-3">
        <StripeCardSplit :stripeError="stripeError" />
      </div>
      <div class="flex items-center justify-end gap-x-2 mt-3">
        <button class="alternative-button text-xs py-2 px-3 text-gray-500 font-bold" @click="cancelEditPayment">Cancel</button>
        <button
          class="primary-button font-bold text-xs py-2 px-3"
          :disabled="savingPayment"
          @click="savePayment"
        >
          <LoadingSpinner v-if="savingPayment" :width="'16px'" :height="'16px'" :background="'transparent'" />
          <span v-else>Save</span>
        </button>
      </div>
    </section>

    <!-- Billed to editing -->
    <section v-if="editingBilling" class="border-t border-gray-200 pt-4">
      <div class="flex items-center justify-between mb-3">
        <h4 class="text-sm font-semibold text-gray-500">Billed to</h4>
        <button class="text-gray-400 hover:text-gray-600" @click="cancelEditBilling">
          <Icon icon="heroicons:arrow-uturn-left" class="text-base" />
        </button>
      </div>

      <div class="bg-white rounded-lg border border-gray-200 p-3 flex items-center justify-between">
        <div class="bg-gray-100 rounded-lg flex flex-col gap-y-0" :class="{ 'border border-red-500': !billingErrors.length }">
          <div class="grid grid-cols-2">
            <input
              type="text"
              placeholder="First Name"
              class="text-sm rounded-tl-md border-0 border-b border-r border-gray-200 px-3 py-2.5 bg-transparent focus:ring-0 focus:border-gray-300 placeholder:text-gray-400"
              :class="{ '!text-red-600 !placeholder:text-red-600 !border-red-500 !bg-red-50': !billingErrors.includes('first_name') }"
              v-model="billingAddress.first_name"
            />
            <input
              type="text"
              placeholder="Last Name"
              class="text-sm rounded-tr-md border-0 border-b border-gray-200 px-3 py-2.5 bg-transparent focus:ring-0 focus:border-gray-300 placeholder:text-gray-400"
              :class="{ '!text-red-600 !placeholder:text-red-600 !border-red-500 !bg-red-50': billingErrors.includes('last_name') }"
              v-model="billingAddress.last_name"
            />
          </div>
          <input
            type="text"
            placeholder="Address line one"
            class="text-sm border-0 border-b border-gray-200 px-3 py-2.5 bg-transparent focus:ring-0 focus:border-gray-300 placeholder:text-gray-400"
            :class="{ '!text-red-600 !placeholder:text-red-600 !border-red-500 !bg-red-50': billingErrors.includes('address_1') }"
            v-model="billingAddress.address_1"
          />
          <input
            type="text"
            placeholder="Address line two (optional)"
            class="text-sm border-0 border-b border-gray-200 px-3 py-2.5 bg-transparent focus:ring-0 focus:border-gray-300 placeholder:text-gray-400"
            v-model="billingAddress.address_2"
          />
          <div class="grid grid-cols-2">
            <input
              type="text"
              placeholder="City"
              class="text-sm border-0 border-b border-r border-gray-200 px-3 py-2.5 bg-transparent focus:ring-0 focus:border-gray-300 placeholder:text-gray-400"
              :class="{ '!text-red-600 !placeholder:text-red-600 !border-red-500 !bg-red-50': billingErrors.includes('city') }"
              v-model="billingAddress.city"
            />
            <input
              type="text"
              :placeholder="billingAddress.country === 'US' ? 'ZIP Code' : 'Postal Code'"
              class="text-sm border-0 border-b border-gray-200 px-3 py-2.5 bg-transparent focus:ring-0 focus:border-gray-300 placeholder:text-gray-400"
              :class="{ '!text-red-600 !placeholder:text-red-600 !border-red-500 !bg-red-50': billingErrors.includes('postal_code') }"
              v-model="billingAddress.postal_code"
            />
          </div>
          <div class="grid grid-cols-2">
            <div class="border-r border-gray-200">
              <input
                type="text"
                placeholder="State"
                class="w-full rounded-bl-md text-sm border-0 px-3 py-2.5 bg-transparent focus:ring-0 focus:border-gray-300 placeholder:text-gray-400"
                :class="{ '!text-red-600 !placeholder:text-red-600 !border-red-500 !bg-red-50': billingErrors.includes('state') }"
                v-model="billingAddress.state"
              />
            </div>
            <input
              type="text"
              placeholder="Country/Region"
              class="text-sm rounded-br-md border-0 px-3 py-2.5 bg-transparent focus:ring-0 focus:border-gray-300 placeholder:text-gray-400"
              :class="{ '!text-red-600 !placeholder:text-red-600 !border-red-500 !bg-red-50': billingErrors.includes('country') }"
              v-model="billingAddress.country"
            />
          </div>
        </div>
      </div>

      <div class="flex items-center justify-end gap-x-2 mt-3">
        <button class="alternative-button text-xs py-2 px-3 text-gray-500 font-bold" @click="cancelEditBilling">Cancel</button>
        <button
          class="primary-button font-bold text-xs py-2 px-3"
          :disabled="savingBilling"
          @click="saveBilling"
        >
          <LoadingSpinner v-if="savingBilling" :width="'16px'" :height="'16px'" :background="'transparent'" />
          <span v-else>Save</span>
        </button>
      </div>
    </section>
  </div>
</template>
